import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { QrCode, ArrowUpRight } from "lucide-react";

export default function Polls() {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("all");
  const [polls, setPolls] = useState([]);

  const load = () => {
    const params = {};
    if (channelId !== "all") params.channel_id = channelId;
    api.get("/polls", { params }).then((r) => setPolls(r.data));
  };

  useEffect(() => { api.get("/channels").then((r) => setChannels(r.data)); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [channelId]);

  const vote = async (pollId, optId) => {
    await api.post(`/polls/${pollId}/vote`, { option_id: optId });
    load();
  };

  return (
    <div className="p-4">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Interactive Polls</div>
          <h1 className="font-display font-bold text-2xl mt-1">Live viewer engagement</h1>
        </div>
        <Select value={channelId} onValueChange={setChannelId}>
          <SelectTrigger className="w-56 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="polls-channel-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800">
            <SelectItem value="all">All channels</SelectItem>
            {channels.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {polls.map((p) => {
          const ch = channels.find((c) => c.id === p.channel_id);
          const maxVotes = Math.max(1, ...p.options.map((o) => o.votes));
          return (
            <div key={p.id} className="panel" data-testid={`poll-${p.id}`}>
              <div className="panel-header">
                <span className="panel-title">{ch?.name || p.channel_id.slice(0, 6)}</span>
                <span className="text-[10px] font-mono text-emerald-400 uppercase">
                  ● {p.status} · {p.total_votes} votes
                </span>
              </div>
              <div className="p-4">
                <div className="font-display font-bold text-lg mb-4">{p.question}</div>
                <div className="space-y-2">
                  {p.options.map((o) => {
                    const pct = ((o.votes / (p.total_votes || 1)) * 100).toFixed(1);
                    return (
                      <div key={o.id}>
                        <div className="flex justify-between text-xs mb-1">
                          <span>{o.label}</span>
                          <span className="font-mono text-zinc-400">{o.votes} · {pct}%</span>
                        </div>
                        <div className="h-6 bg-zinc-950 border border-zinc-800 rounded-sm relative overflow-hidden">
                          <div
                            className="h-full transition-all"
                            style={{ width: `${(o.votes / maxVotes) * 100}%`, background: "var(--tenant-primary)" }}
                          />
                          <button
                            onClick={() => vote(p.id, o.id)}
                            data-testid={`poll-vote-${p.id}-${o.id}`}
                            className="absolute inset-0 opacity-0 hover:opacity-100 bg-black/40 text-[10px] font-mono uppercase text-white"
                          >
                            + vote
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 flex items-center gap-3 border-t border-zinc-800 pt-3">
                  <div className="w-16 h-16 bg-white rounded-sm p-1.5">
                    {/* QR code placeholder */}
                    <div className="w-full h-full bg-black grid grid-cols-5 gap-0.5">
                      {Array.from({ length: 25 }).map((_, i) => (
                        <div key={i} style={{ background: (i * 7 + p.id.charCodeAt(0)) % 3 ? "white" : "black" }} />
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-zinc-400">
                    <div className="font-mono text-[10px] text-zinc-500 uppercase">Viewer QR</div>
                    <div>Scan to vote from the broadcast</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
